import classNames from "classnames"
import { format } from "date-fns"
import React, { useRef, useState } from "react"
import Popover from "../../Popover"
import { tooltipConfig } from "../../Popover/tooltipConfig"

type CalendarRangePickerDayPropsType = {
  date: Date
  isEditingStartDate: boolean
  isEditingEndDate: boolean
  isToday?: boolean
  isStartDay?: boolean
  isEndDay?: boolean
  isSelected?: boolean
  isInvalid?: boolean
  isDisabled?: boolean
  isRange?: boolean
  isActive?: boolean
  hasNotification?: boolean
  tooltipMessage?: string
  hoverTooltipMessage?: string
  onSelect: (date: Date) => void
  onMouseEnter: (date: Date, disabled: boolean) => void
  onMouseLeave?: (date: Date) => void
}

function CalendarRangePickerDay({
  date,
  isToday,
  isStartDay,
  isEndDay,
  isSelected,
  isDisabled,
  isInvalid,
  isRange,
  isActive,
  hasNotification,
  tooltipMessage,
  onSelect,
  onMouseEnter,
  onMouseLeave,
  hoverTooltipMessage,
}: CalendarRangePickerDayPropsType) {
  const dayRef = useRef<HTMLButtonElement>(null)
  const [isHovered, setIsHovered] = useState(false)

  const handleSelectDate = () => {
    !isDisabled && !isInvalid && onSelect(new Date(date))
  }

  const jsx = (
    <button
      ref={dayRef}
      className={classNames("cobalt-CalendarRangePicker__day", {
        "cobalt-CalendarRangePicker__day--today": isToday,
        "cobalt-CalendarRangePicker__day--startDay": isStartDay,
        "cobalt-CalendarRangePicker__day--endDay": isEndDay,
        "cobalt-CalendarRangePicker__day--range": isRange,
        "cobalt-CalendarRangePicker__day--selected": isSelected,
        "cobalt-CalendarRangePicker__day--active": isActive,
        "cobalt-CalendarRangePicker__day--invalid": isInvalid,
        "cobalt-CalendarRangePicker__day--disabled": isDisabled,
      })}
      onMouseEnter={() => {
        onMouseEnter(date, !!isDisabled)
        setIsHovered(true)
      }}
      onMouseLeave={() => {
        onMouseLeave?.(date)
        setIsHovered(false)
      }}
      onClick={handleSelectDate}
      data-day={format(date, "yyyy-MM-dd")}
      type="button"
    >
      {date.getDate()}
      {hasNotification && (
        <div className="cobalt-CalendarRangePicker__day-notification-container">
          <div className="cobalt-CalendarRangePicker__day-notification"></div>
        </div>
      )}
    </button>
  )

  const message = tooltipMessage ?? hoverTooltipMessage

  if (!message) return jsx

  const isControlled = !!tooltipMessage

  return (
    <>
      {jsx}
      <Popover
        {...tooltipConfig}
        targetRef={dayRef}
        isOpen={isControlled || isHovered}
        onOpenChange={isControlled ? undefined : setIsHovered}
        placement={isControlled && !isStartDay ? "bottom" : "top"}
        responsive={false}
      >
        <div className="c-text-body-xs">{message}</div>
      </Popover>
    </>
  )
}

function areEqual(
  prevProps: CalendarRangePickerDayPropsType,
  nextProps: CalendarRangePickerDayPropsType,
) {
  return (
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.isStartDay === nextProps.isStartDay &&
    prevProps.isEndDay === nextProps.isEndDay &&
    prevProps.isInvalid === nextProps.isInvalid &&
    prevProps.isRange === nextProps.isRange &&
    prevProps.hasNotification === nextProps.hasNotification &&
    prevProps.isDisabled === nextProps.isDisabled &&
    prevProps.isEditingStartDate === nextProps.isEditingStartDate &&
    prevProps.isEditingEndDate === nextProps.isEditingEndDate
  )
}

const MemoizedComponent = React.memo(CalendarRangePickerDay, areEqual)
MemoizedComponent.displayName = "CalendarRangePickerDay"

export { MemoizedComponent as CalendarRangePickerDay }
